import React from 'react';
import {eye, edit, corruptedFile} from 'assets/images/images';
import {
  TableLayout,
  Loader,
  PageLimit,
  Pagination,
  Filter,
  FilterButton,
  EmptyState,
} from 'components';
import {filterOutEmptyValues, generateQueryParams} from 'helpers';
import {CopyItem} from 'components/CopyItem';
import moment from 'moment';
import {shortenLargeNumbers} from 'helpers/shortenLargeNumbers';

function BaasWalletTable({
  wallets = [],
  isLoading = false,
  customerId = null,
  onViewWallet = () => {},
  onEditWallet = () => {},
  EmptyState: CustomEmptyState = null,

  totalPages = 1,
  currentPage = 1,
  onPageChange = () => {},

  limit = 10,
  onLimitChange = () => {},
  totalItems = 0,

  openFilter = false,
  setOpenFilter = () => {},
  filters = {},
  setFilters = () => {},
  filterOptions = [],
  onResetFilter = () => {},
  navigate = null,
  searchText = '',
  setSearchText = null,
  initialFilters = {},

  className = '',
  showPagination = true,
  showFilter = true,
  showEdit = true,
}) {
  const handleFilter = (selectedOptions, shouldDownload = false) => {
    if (!navigate) return;

    const filteredOptions = filterOutEmptyValues(selectedOptions);
    const queryParams = generateQueryParams(filteredOptions);
    const {status} = filteredOptions;
    if (Array.isArray(status)) {
      filteredOptions.status = status.join(',');
    }
    let baseUrl = '';
    if (queryParams) {
      baseUrl += `?${queryParams}`;
    }

    if (shouldDownload) {
      filteredOptions.download = true;
      setFilters({
        ...filters,
        ...filteredOptions,
      });
    } else {
      navigate(baseUrl);
    }

    setFilters(filteredOptions);
    setOpenFilter(false);
  };

  const isNotFiltered = !isLoading && (!wallets || wallets.length === 0);
  const isFiltered =
    isNotFiltered &&
    (Object.values(filters).some(s => s) ||
      (searchText && searchText.length > 0));

  const DefaultEmptyState = () => {
    if (isFiltered) {
      return (
        <EmptyState
          height={45}
          noBtn
          customBtn={
            <button
              onClick={() => {
                navigate && navigate('');
                setFilters(initialFilters || {});
                setSearchText && setSearchText('');
                setOpenFilter(true);
              }}
              className="text-brandBlue p-4 text-sm font-medium"
            >
              Update preferences
            </button>
          }
          heading="No Wallet Records Found"
          body="Oops, it looks like there are no wallet records that match the filters/search term you've selected. Please try adjusting your filters."
        />
      );
    }

    return (
      <EmptyState
        height={45}
        noBtn
        className="w-full"
        src={corruptedFile}
        heading="No Wallet Records Found"
        body="Sorry, it appears there are no wallet records available for this company."
        maxWidth="max-w-[500px]"
      />
    );
  };

  return (
    <>
      {showFilter && (
        <Filter
          openFilter={openFilter}
          setOpenFilter={setOpenFilter}
          handleFilter={handleFilter}
          resetUrl={onResetFilter}
          filterOptions={filterOptions}
        />
      )}

      <TableLayout negativeMargins className={`w-full ${className}`}>
        {isLoading ? (
          <Loader height={45} />
        ) : (
          <>
            <thead className="text-small font-semibold uppercase">
              <tr className="bg-white80">
                <td className="p-5 pl-12 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Wallet name
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  Bank name
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  account number
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  date created
                </td>
                <td className="p-5 pl-0 font-medium text-left text-[10px] text-grey whitespace-nowrap">
                  current balance
                </td>
                <td className="p-5 pl-0"></td>
              </tr>
            </thead>
            <tbody className="text-[13px] text-body bg-white">
              {wallets.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-4 text-center">
                    {CustomEmptyState ? (
                      <CustomEmptyState />
                    ) : (
                      <DefaultEmptyState />
                    )}
                  </td>
                </tr>
              ) : (
                wallets.map((wallet, index) => (
                  <tr
                    key={wallet?.wallet_id || index}
                    className="text-left border-b bg-white border-grey60"
                  >
                    <td className="p-4 pl-12 whitespace-nowrap">
                      {`${wallet?.first_name || ''} ${
                        wallet?.last_name || '-'
                      }` ||
                        wallet?.name ||
                        '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {wallet?.bank_name || '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap flex items-center">
                      {wallet?.account_number || '-'}
                      {wallet?.account_number?.length > 0 && (
                        <CopyItem value={wallet?.account_number} />
                      )}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {(wallet?.date_created &&
                        moment(wallet?.date_created).format(
                          'DD MMM YYYY h:m',
                        )) ||
                        wallet?.date ||
                        '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap">
                      {typeof wallet?.wallet_amount !== 'undefined'
                        ? shortenLargeNumbers(
                            wallet?.wallet_amount || 0,
                            'NGN',
                            {startAt: 'M'},
                          )
                        : wallet?.balance || '-'}
                    </td>
                    <td className="p-4 pl-0 whitespace-nowrap flex items-center gap-4 justify-between">
                      <div className="flex items-center gap-5">
                        <button onClick={() => onViewWallet(wallet)}>
                          <img src={eye} alt="View" />
                        </button>
                        {showEdit && (
                          <button onClick={() => onEditWallet(wallet)}>
                            <img src={edit} alt="Edit" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </>
        )}
      </TableLayout>

      {showPagination && !isLoading && wallets.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-4 mt-12 sm:gap-0">
          <PageLimit
            onLimitChange={onLimitChange}
            limit={limit}
            total={totalItems}
            length={wallets.length}
          />

          <Pagination
            total={totalPages}
            current={currentPage}
            onPageChange={onPageChange}
          />
        </div>
      )}
    </>
  );
}

BaasWalletTable.FilterButton = function BaasWalletFilterButton({
  openFilter,
  setOpenFilter,
  className = '',
}) {
  return (
    <FilterButton
      openFilter={openFilter}
      setOpenFilter={setOpenFilter}
      className={`h-[48px] justify-center w-[130px] ${className}`}
    />
  );
};

export default BaasWalletTable;
